import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { getFieldError } from '@app/pages/hiring-manager/hiring-request/hiring-request.helper';
import {
  EXCEL_FILE_EXTENSION,
  uploadControls,
} from '@app/shared/constants/constants';
import {
  quarterlyFeedback,
  RoutesEnum,
} from '@app/shared/constants/global.enum';
import { FileUploadService } from '@app/shared/services/file-upload.service';
import { ToasterService } from '@app/shared/services/toaster.service';
import { QuarterlyFeedbackFormService } from './quarterly-feedback-form.service';
import { QuarterlyFeedback } from '@app/shared/interfaces/quarterly-feedback.model';
import { SearchService } from '@app/shared/services/search.service';

@Component({
  selector: 'app-quarterly-feedback-form',
  templateUrl: './quarterly-feedback-form.component.html',
  styleUrl: './quarterly-feedback-form.component.scss',
})
export class QuarterlyFeedbackFormComponent implements OnInit {
  selectedFile: string | null = null;
  quarterlyFeedbackForm!: FormGroup;
  isSubmitted: boolean = false;
  fileError: string | null = null;
  fileSize: string | null = null;
  quarterlyFeedback = quarterlyFeedback;
  routesEnum = RoutesEnum;
  vendorId: string | null = null;
  vendorName: string | null = null;
  selectedVendor: any = null;
  showVendorProfile: boolean = false;
  currentQuarterAndYear: { quarter: string | number; year: number } = { quarter: '', year: 0 }

  uploadedFileNames: { [key: string]: string } = {};
  @ViewChild('msaUploadFileUpload') msaUploadFileUpload!: any;
  @ViewChild('assetUploadFileUpload') assetUploadFileUpload!: any;
  @ViewChild('sowUploadFileUpload') sowUploadFileUpload!: any;
  @ViewChild('invoiceUploadFileUpload') invoiceUploadFileUpload!: any;

  selectedFiles: { [key: string]: File | null } = {
    msaUpload: null,
    assetUpload: null,
    sowUpload: null,
    invoiceUpload: null,
  };
  fileErrors: { [key: string]: string } = {
    msaUpload: '',
    assetUpload: '',
    sowUpload: '',
    invoiceUpload: '',
  };

  constructor(
    private quarterly: FormBuilder,
    private router: Router,
    private fileUploadService: FileUploadService,
    private toasterService: ToasterService,
    private quarterlyFeedbackFormService: QuarterlyFeedbackFormService,
    private  searchService: SearchService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.vendorId = this.route.snapshot.paramMap.get('vendorId') || null;
    this.vendorName = this.route.snapshot.queryParamMap.get('vendorName') || null;
    this.selectedVendor = history.state.vendorData;
    this.currentQuarterAndYear = this.searchService.getCurrentQuarterAndYear();    
  }

  initializeForm(): void {
    this.quarterlyFeedbackForm = this.quarterly.group({
      msaRateCard: ['', Validators.required],
      msaUpload: ['', Validators.required],
      assetCompliance: ['', Validators.required],
      assetUpload: ['', Validators.required],
      sowCompliance: ['', Validators.required],
      sowUpload: ['', Validators.required],
      invoiceCompliance: ['', Validators.required],
      invoiceUpload: ['', Validators.required],
    });
  }

  onFileSelected(file: File, controlName: string) {
    if (!EXCEL_FILE_EXTENSION.some(type => file.type === type)) {
      this.fileErrors[controlName] = quarterlyFeedback.invalidType;
      this.selectedFiles[controlName] = null;
      this.quarterlyFeedbackForm.get(controlName)?.setValue('');
      return;
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 3) {
      this.fileSize = `${Math.floor(fileSizeInMB)} MB`;
      this.fileErrors[controlName] = quarterlyFeedback.fileExceedsSize;
      this.selectedFiles[controlName] = null;
      this.quarterlyFeedbackForm.get(controlName)?.setValue('');
      return;
    } else if (file.size > 1024) {
      this.fileSize = `${Math.floor(file.size / 1024)} KB`;
    }

    this.selectedFiles[controlName] = file;
    this.fileErrors[controlName] = '';
    this.fileUploadService.uploadFile(file, controlName).subscribe((res) => {
      if (res?.success && res?.filePath) {
        this.quarterlyFeedbackForm.get(controlName)?.setValue(file);
        this.uploadedFileNames[controlName] = res?.filePath;
      } else {
        this.toasterService.showError(res?.message, 'Error');
        this.quarterlyFeedbackForm.get(controlName)?.setValue('');
        this.selectedFiles[controlName] = null;
        this.uploadedFileNames[controlName] = '';
      }
    });
  }

  onFileRemoved(controlName: string) {
    this.selectedFiles[controlName] = null;
    this.quarterlyFeedbackForm.get(controlName)?.setValue('');
    const fileUploadRefs: {
      [key: string]: { resetFileInput: () => void } | undefined;
    } = {
      msaUpload: this.msaUploadFileUpload,
      assetUpload: this.assetUploadFileUpload,
      sowUpload: this.sowUploadFileUpload,
      invoiceUpload: this.invoiceUploadFileUpload,
    };
    const ref = fileUploadRefs[controlName];
    if (ref && typeof ref.resetFileInput === 'function') {
      ref.resetFileInput();
    }
  }

  onSave(): void {
    this.isSubmitted = true;

    uploadControls?.forEach((controlName) => {
      if (!this.selectedFiles[controlName]) {
        this.fileErrors[controlName] = quarterlyFeedback.validationError;
      }
    });

    const form = this.quarterlyFeedbackForm;
    const filesValid = uploadControls?.every(
      (control) => !!this.selectedFiles[control],
    );

    if (form.valid && filesValid) {
      const formValue = { ...form.value };
      uploadControls.forEach((control) => {
        formValue[control] = this.uploadedFileNames[control] || '';
      });
      
      const feedback: QuarterlyFeedback = {
        msaRateCardCompliance: formValue?.msaRateCard?.toString() || '',
        msaRateCardComplianceUrl: formValue?.msaUpload,
        assestCompliance: formValue?.assetCompliance?.toString() || '',
        assestComplianceUrl: formValue?.assetUpload,
        sowCompliance: formValue?.sowCompliance?.toString() || '',
        sowComplianceUrl: formValue?.sowUpload,
        invoiceCompliance: formValue?.invoiceCompliance?.toString() || '',
        invoiceComplianceUrl: formValue?.invoiceUpload,
        vendorId: Number(this.vendorId),
        yearId: this.currentQuarterAndYear?.year,
        quarterId: Number(this.currentQuarterAndYear?.quarter),
      };

      this.quarterlyFeedbackFormService
        .saveFeedbackFormData(feedback)
        .subscribe(
          () => {
            this.toasterService.showSuccess(
              quarterlyFeedback.successMsg,
              'Success',
            );
              this.routingToVendorManagement();
          },  
          () => {
            this.toasterService.showError(quarterlyFeedback.errorMsg, 'Error');
          },
        );
    }
  }

  onCancel(): void {
   this.routingToVendorManagement();
  }

  routingToVendorManagement(): void {
    this.router.navigate(
      [RoutesEnum.OpsVendorManagement, RoutesEnum.vendorsList],
      { state: { vendorData: this.selectedVendor } }
    );
  }

  isFieldValid(fieldName: string): boolean {
    const control = this.quarterlyFeedbackForm.get(fieldName);
    if (this.isSubmitted || control?.touched) {
      return control?.valid ?? false;
    }
    return true;
  }

  getFieldError(fieldName: string): string {
    const control = this.quarterlyFeedbackForm.get(fieldName);
    return getFieldError(control);
  }

  navigateToVendorManagement(): void {
    this.router.navigate([RoutesEnum.OpsVendorManagement]);
  }
}
